import { useState, useRef, useCallback, useEffect, Component, ReactNode } from 'react'
import { router } from '@inertiajs/react'
import { Meeting, ActionItem, User, MeetingAttendee, WNTKEntry, TopicResolution, MeetingSection, MeetingRating, ConflictResponse } from '../../types'
import StatNumber from '../shared/StatNumber'
import { sortPeople, sortByPerson } from '../../utils/peopleSort'

// Task #645: Healthy Conflict answers are three-state (Yes / No / N/A). The
// server normalizes the booleans stored on pre-N/A meetings before
// serializing, but props can still arrive from an older payload, so every
// read on this screen goes through here rather than comparing to `true`.
const CONFLICT_LABELS: Record<ConflictResponse, string> = { yes: 'Yes', no: 'No', na: 'N/A' }

function normalizeConflictResponse(value: ConflictResponse | boolean | null | undefined): ConflictResponse | null {
  if (value === true) return 'yes'
  if (value === false) return 'no'
  if (value === 'yes' || value === 'no' || value === 'na') return value
  return null
}

function normalizeConflictResponses(
  responses: Record<string, ConflictResponse | boolean> | null | undefined
): Record<string, ConflictResponse> {
  const result: Record<string, ConflictResponse> = {}
  Object.entries(responses || {}).forEach(([userId, value]) => {
    const normalized = normalizeConflictResponse(value)
    if (normalized) result[userId] = normalized
  })
  return result
}

class CheckOutErrorBoundary extends Component<{ stepName: string; children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { stepName: string; children: ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }
  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error(`[CheckOut] Error in "${this.props.stepName}":`, error.message, info.componentStack)
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-[rgba(237,28,36,0.15)] border border-[#ED1C24]/20 rounded-lg p-4">
          <h4 className="font-semibold text-[#ED1C24] mb-1" style={{ fontSize: '14px' }}>Something went wrong in {this.props.stepName}</h4>
          <p className="text-[#ED1C24]" style={{ fontSize: '14px' }}>{this.state.error?.message}</p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-2 text-[#ED1C24] underline hover:no-underline"
            style={{ fontSize: '14px' }}
          >
            Try again
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

interface Props {
  meeting: Meeting
  new_action_items: ActionItem[]
  carriedOverItems?: ActionItem[]
  meetingId: number
  readOnly?: boolean
  canFacilitate: boolean
  teamMembers: User[]
  meetingAttendees: MeetingAttendee[]
  currentUserId?: number
  meetingTopicResolutions?: TopicResolution[]
  sections?: MeetingSection[]
  meetingRatings?: MeetingRating[]
  summaryRecipients?: { id: number; name: string; email: string }[]
}

type CheckOutStep = 'wntk' | 'ratings' | 'conflict' | 'summary' | 'end_meeting'

const STEPS: { key: CheckOutStep; label: string }[] = [
  { key: 'wntk', label: 'Who Needs to Know What' },
  { key: 'ratings', label: 'Rate This Meeting' },
  { key: 'conflict', label: 'Healthy Conflict' },
  { key: 'summary', label: 'Meeting Summary' },
  { key: 'end_meeting', label: 'End Meeting' },
]

export default function SectionCheckOut({
  meeting, new_action_items, carriedOverItems, meetingId, readOnly, canFacilitate,
  teamMembers, meetingAttendees, currentUserId, meetingTopicResolutions,
  sections, meetingRatings: meetingRatingsProp, summaryRecipients
}: Props) {
  const [activeStep, setActiveStep] = useState<CheckOutStep>('wntk')
  const activeStepIndex = STEPS.findIndex(s => s.key === activeStep)

  const goNext = () => {
    if (activeStepIndex < STEPS.length - 1) {
      setActiveStep(STEPS[activeStepIndex + 1].key)
    }
  }

  const goPrev = () => {
    if (activeStepIndex > 0) {
      setActiveStep(STEPS[activeStepIndex - 1].key)
    }
  }

  const completedThisMeeting = (new_action_items || []).filter(item => item.status === 'completed' || item.id)
  const meetingRatings = meeting.ratings || []
  const cascadingMessages = meeting.communications || []

  const handleEndMeeting = () => {
    if (confirm('This will end the meeting and send the summary email. Continue?')) {
      router.post(`/meetings/${meetingId}/end_meeting`)
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00')
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  if (readOnly && meeting.status === 'completed') {
    const allRatings = meetingRatingsProp || []
    const presentRatings = allRatings.filter(r => !r.is_absent)
    const absentRatings = allRatings.filter(r => r.is_absent)
    // Task #645: normalize first so a meeting recorded before N/A existed
    // still reads as Yes/No here.
    const conflictData = normalizeConflictResponses(meeting.conflict_responses)
    // Task #613: the recap's conflict chips read alphabetically by person.
    const conflictEntries = sortByPerson(
      Object.entries(conflictData),
      ([userId]) => teamMembers.find(m => String(m.id) === String(userId))
    )
    const yesCount = conflictEntries.filter(([, v]) => v === 'yes').length
    const noCount = conflictEntries.filter(([, v]) => v === 'no').length
    const naCount = conflictEntries.filter(([, v]) => v === 'na').length
    const wntk = meeting.communications || []
    const resolutions = meetingTopicResolutions || []
    const openItems = completedThisMeeting.filter(i => i.status === 'open')
    const completedItems = completedThisMeeting.filter(i => i.status === 'completed')
    const carried = carriedOverItems || []
    const meetingSections = sections || []

    const totalElapsed = meetingSections.reduce((sum, s) => sum + (s.elapsed_seconds || 0), 0)
    const formatElapsed = (secs: number) => {
      const m = Math.floor(secs / 60)
      const s2 = secs % 60
      if (m === 0) return `${s2}s`
      return s2 > 0 ? `${m}m ${s2}s` : `${m}m`
    }
    const formatTotalTime = (secs: number) => {
      const h = Math.floor(secs / 3600)
      const m = Math.floor((secs % 3600) / 60)
      if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`
      return `${m}m`
    }

    const attendingUsers = meetingAttendees.filter(a => a.attending).map(a => a.user)
    // Task #613: the completed-meeting recap lists people alphabetically.
    const attendeeList = sortPeople(attendingUsers.length > 0 ? attendingUsers : teamMembers)

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-xl font-semibold text-text-primary tracking-wide uppercase mb-2">Meeting Summary</h3>
          <p className="text-base text-text-muted">Review of the completed meeting.</p>
        </div>

        <div className="bg-[#F5F5F5] rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <svg className="w-10 h-10 text-[#15532E] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-lg font-semibold text-text-primary">Meeting Completed</h4>
              <p className="text-sm text-text-muted">
                {meeting.ended_at ? new Date(meeting.ended_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : 'N/A'}
                {meeting.ended_at && ` at ${new Date(meeting.ended_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-3 text-center min-w-0">
              <StatNumber size="xs" className="text-text-primary text-center">{formatTotalTime(totalElapsed)}</StatNumber>
              <div className="text-xs text-text-muted uppercase tracking-wide mt-1">Total Duration</div>
            </div>
            <div className="bg-white rounded-lg p-3 text-center min-w-0">
              <StatNumber size="xs" className="text-text-primary text-center">{attendeeList.length}</StatNumber>
              <div className="text-xs text-text-muted uppercase tracking-wide mt-1">Attendees</div>
            </div>
            <div className="bg-white rounded-lg p-3 text-center min-w-0">
              <StatNumber size="xs" className="text-pinnacle-red text-center">{meeting.average_rating != null ? Number(meeting.average_rating).toFixed(1) : '—'}</StatNumber>
              <div className="text-xs text-text-muted uppercase tracking-wide mt-1">Avg Rating</div>
            </div>
            <div className="bg-white rounded-lg p-3 text-center min-w-0">
              <StatNumber size="xs" className="text-text-primary text-center">{openItems.length + completedItems.length}</StatNumber>
              <div className="text-xs text-text-muted uppercase tracking-wide mt-1">Action Items</div>
            </div>
          </div>
          {meeting.summary_sent_at && (
            <div className="mt-3 flex items-center gap-2 text-sm text-text-muted">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
              Summary email sent
            </div>
          )}
        </div>

        {attendeeList.length > 0 && (
          <div className="bg-white border border-stone/10 rounded-xl p-5">
            <h4 className="text-sm font-bold uppercase tracking-widest text-text-primary mb-3">Attendees</h4>
            <div className="flex flex-wrap gap-2">
              {attendeeList.map(user => (
                <div key={user.id} className="flex items-center gap-2 bg-[#F5F5F5] rounded-full px-3 py-1.5">
                  <div className="w-6 h-6 rounded-full bg-pinnacle-red flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                    {user.first_name?.[0]}{user.last_name?.[0]}
                  </div>
                  <span className="text-sm text-text-primary font-medium">{user.first_name} {user.last_name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {meetingSections.length > 0 && (
          <div className="bg-white border border-stone/10 rounded-xl p-5">
            <h4 className="text-sm font-bold uppercase tracking-widest text-text-primary mb-3">Time per Section</h4>
            <div className="space-y-2">
              {meetingSections.map(section => {
                const planned = section.duration_minutes * 60
                const actual = section.elapsed_seconds || 0
                const overTime = actual > planned
                const pct = planned > 0 ? Math.min((actual / planned) * 100, 100) : 0
                return (
                  <div key={section.id} className="flex items-center gap-3">
                    <div className="w-40 text-sm text-text-primary font-medium truncate flex-shrink-0">{section.display_name}</div>
                    <div className="flex-1 bg-[#F5F5F5] rounded-full h-2.5 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${overTime ? 'bg-[#ED1C24]' : 'bg-[#15532E]'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="flex items-center gap-1 w-28 justify-end flex-shrink-0">
                      <span className={`text-sm font-semibold ${overTime ? 'text-[#ED1C24]' : 'text-text-primary'}`}>
                        {formatElapsed(actual)}
                      </span>
                      <span className="text-xs text-text-muted">/ {section.duration_minutes}m</span>
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="mt-3 pt-3 border-t border-stone/10 flex justify-between items-center">
              <span className="text-sm font-bold text-text-primary uppercase tracking-wide">Total</span>
              <span className="text-sm font-bold text-text-primary">{formatTotalTime(totalElapsed)}</span>
            </div>
          </div>
        )}

        {presentRatings.length > 0 && (
          <div className="bg-white border border-stone/10 rounded-xl p-5">
            <h4 className="text-sm font-bold uppercase tracking-widest text-text-primary mb-3">Meeting Ratings</h4>
            <div className="flex items-center gap-4 mb-4">
              <div className="text-4xl font-bold text-pinnacle-red">{meeting.average_rating != null ? Number(meeting.average_rating).toFixed(1) : '—'}</div>
              <div>
                <div className="text-sm font-semibold text-text-primary">Average Rating</div>
                <div className="text-xs text-text-muted">{presentRatings.length} rating{presentRatings.length !== 1 ? 's' : ''} submitted (out of 6)</div>
              </div>
            </div>
            <div className="space-y-2">
              {presentRatings.map(r => (
                <div key={r.id} className="flex items-center gap-3 py-1.5">
                  <div className="w-7 h-7 rounded-full bg-pinnacle-red flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                    {r.user_initials}
                  </div>
                  <span className="text-sm text-text-primary font-medium w-32 truncate">{r.user_name}</span>
                  <div className="flex gap-1 flex-1">
                    {[1, 2, 3, 4, 5, 6].map(n => (
                      <div
                        key={n}
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                          n <= r.rating ? 'bg-pinnacle-red text-white' : 'bg-[#F5F5F5] text-text-muted'
                        }`}
                      >
                        {n}
                      </div>
                    ))}
                  </div>
                  <span className="text-sm font-bold text-text-primary w-8 text-right">{r.rating}</span>
                </div>
              ))}
            </div>
            {absentRatings.length > 0 && (
              <div className="mt-3 pt-3 border-t border-stone/10">
                <p className="text-xs text-text-muted">
                  Marked absent: {absentRatings.map(r => r.user_name).join(', ')}
                </p>
              </div>
            )}
          </div>
        )}

        {conflictEntries.length > 0 && (
          <div className="bg-white border border-stone/10 rounded-xl p-5">
            <h4 className="text-sm font-bold uppercase tracking-widest text-text-primary mb-3">Healthy Conflict</h4>
            <div className="flex items-center gap-6 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#15532E] flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm text-text-primary font-semibold">{yesCount} Yes</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#ED1C24] flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <span className="text-sm text-text-primary font-semibold">{noCount} No</span>
              </div>
              {/* Task #645: N/A joins the recap counts with the same neutral
                  treatment the live step uses. */}
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#351E1D] flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
                  </svg>
                </div>
                <span className="text-sm text-text-primary font-semibold">{naCount} N/A</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {conflictEntries.map(([userId, conflictResponse]) => {
                const user = teamMembers.find(m => String(m.id) === String(userId))
                const chipTone = conflictResponse === 'yes'
                  ? 'bg-[rgba(21,83,46,0.1)] text-[#15532E]'
                  : conflictResponse === 'no'
                    ? 'bg-[rgba(237,28,36,0.1)] text-[#ED1C24]'
                    : 'bg-[#351E1D]/10 text-text-primary'
                return (
                  <div key={userId} className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ${chipTone}`}>
                    {user ? `${user.first_name} ${user.last_name?.[0]}.` : `User ${userId}`}
                    <span className="font-bold">{CONFLICT_LABELS[conflictResponse]}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {resolutions.length > 0 && (
          <div className="bg-white border border-stone/10 rounded-xl p-5">
            <h4 className="text-sm font-bold uppercase tracking-widest text-text-primary mb-3">
              Topics Resolved ({resolutions.length})
            </h4>
            <div className="space-y-2">
              {resolutions.map((r, i) => (
                <div key={i} className="flex items-start gap-3 py-2 border-b border-stone/5 last:border-0">
                  <svg className="w-5 h-5 text-[#15532E] mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-text-primary">{r.topic_title}</span>
                    {r.resolution_notes && (
                      <p className="text-xs text-text-muted mt-0.5">{r.resolution_notes}</p>
                    )}
                  </div>
                  <span className="text-xs bg-[#F5F5F5] text-text-muted px-2 py-0.5 rounded-full capitalize flex-shrink-0">
                    {r.resolution_type?.replace(/_/g, ' ')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {openItems.length > 0 && (
          <div className="bg-white border border-stone/10 rounded-xl p-5">
            <h4 className="text-sm font-bold uppercase tracking-widest text-text-primary mb-3">
              Action Items Created ({openItems.length})
            </h4>
            <div className="space-y-2">
              {openItems.map(item => (
                <div key={item.id} className="flex items-center justify-between py-2 border-b border-stone/5 last:border-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-5 h-5 rounded-full border-2 border-charcoal/20 flex-shrink-0" />
                    {item.owner && (
                      <div className="w-7 h-7 rounded-full bg-pinnacle-red flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                        {item.owner.first_name?.[0]}{item.owner.last_name?.[0]}
                      </div>
                    )}
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-text-primary block truncate">{item.title}</span>
                      {item.owner && <span className="text-xs text-text-muted">{item.owner.full_name}</span>}
                    </div>
                  </div>
                  {item.due_date && (
                    <span className="text-xs text-text-muted flex-shrink-0 ml-2">Due {formatDate(item.due_date)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {carried.length > 0 && (
          <div className="bg-white border border-stone/10 rounded-xl p-5">
            <h4 className="text-sm font-bold uppercase tracking-widest text-text-primary mb-3">
              Carried Over ({carried.length})
            </h4>
            <div className="space-y-2">
              {carried.map(item => (
                <div key={item.id} className="flex items-center justify-between py-2 border-b border-stone/5 last:border-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <svg className="w-5 h-5 text-[#E5A100] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12.75 15l3-3m0 0l-3-3m3 3h-7.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {item.owner && (
                      <div className="w-7 h-7 rounded-full bg-pinnacle-red flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                        {item.owner.first_name?.[0]}{item.owner.last_name?.[0]}
                      </div>
                    )}
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-text-primary block truncate">{item.title}</span>
                      {item.owner && <span className="text-xs text-text-muted">{item.owner.full_name}</span>}
                    </div>
                  </div>
                  {item.due_date && (
                    <span className="text-xs text-text-muted flex-shrink-0 ml-2">Due {formatDate(item.due_date)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {wntk.length > 0 && (
          <div className="bg-white border border-stone/10 rounded-xl p-5">
            <h4 className="text-sm font-bold uppercase tracking-widest text-text-primary mb-3">
              Who Needs to Know What ({wntk.length})
            </h4>
            <div className="space-y-2">
              {wntk.map((entry, i) => (
                <div key={entry.id || i} className="flex items-start gap-3 py-2 border-b border-stone/5 last:border-0">
                  <svg className="w-5 h-5 text-text-primary mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-text-primary">
                      <span className="font-semibold">{entry.who_name || 'Someone'}</span>
                      {' tells '}
                      <span className="font-semibold">{entry.tells_whom}</span>
                    </span>
                    <p className="text-sm text-text-muted mt-0.5">{entry.about_what}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Task #613: check-out rating and conflict rows read alphabetically. This
  // is not the facilitator's drag order (that stays custom in meeting setup,
  // check-in, set-the-stage and the meeting sidebar).
  const attendees = sortPeople(meetingAttendees.length > 0
    ? meetingAttendees.filter(a => a.attending).map(a => a.user)
    : teamMembers.filter(m => m.role !== 'guide' || m.guide_user_privileges))

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-xl font-semibold text-text-primary tracking-wide uppercase mb-2">Check-Out</h3>
        <p className="text-base text-text-muted">Summarize commitments, conflict check, rate meeting 1–6.</p>
      </div>

      <CheckOutErrorBoundary stepName="Commitments">
        <div>
          <h4 className="text-sm font-bold uppercase tracking-widest text-text-primary mb-3">Commitments from This Meeting</h4>
          {completedThisMeeting.length === 0 ? (
            <p className="text-sm text-text-muted italic">No new action items created during this meeting.</p>
          ) : (
            <div className="space-y-2">
              {completedThisMeeting.map(item => (
                <div key={item.id} className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-stone/10">
                  <div className="flex items-center gap-3">
                    {item.owner && (
                      <div className="w-8 h-8 rounded-full bg-pinnacle-red flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {item.owner.first_name?.[0]}{item.owner.last_name?.[0]}
                      </div>
                    )}
                    <div>
                      <span className="font-medium text-text-primary text-sm">{item.title}</span>
                      {item.owner && <span className="text-sm text-text-muted ml-2">{item.owner.full_name}</span>}
                    </div>
                  </div>
                  {item.due_date && <span className="text-sm text-text-muted">Due {formatDate(item.due_date)}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </CheckOutErrorBoundary>

      <div className="flex flex-wrap items-center gap-1 pb-2 mb-6">
        {STEPS.map((step, idx) => {
          const isActive = activeStep === step.key
          const isCompleted = STEPS.findIndex(s => s.key === activeStep) > idx
          return (
            <div key={step.key} className="flex items-center min-w-0">
              <button
                onClick={() => !readOnly && setActiveStep(step.key)}
                className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg font-medium transition-colors min-w-0 ${
                  isActive
                    ? 'bg-[#ED1C24]/10 text-[#ED1C24] border border-[#ED1C24]/20'
                    : isCompleted
                      ? 'bg-[#2D7A4A]/10 text-[#2D7A4A] cursor-pointer'
                      : 'bg-[#351E1D]/5 text-text-secondary cursor-pointer'
                }`}
                style={{ fontSize: '13px' }}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center font-bold flex-shrink-0 ${
                  isActive ? 'bg-[#ED1C24] text-white' : isCompleted ? 'bg-[#15532E] text-white' : 'bg-[#351E1D]/20 text-white'
                }`} style={{ fontSize: '12px' }}>
                  {isCompleted ? (
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : idx + 1}
                </span>
                <span className="truncate">{step.label}</span>
              </button>
              {idx < STEPS.length - 1 && (
                <div className={`w-3 h-0.5 flex-shrink-0 ${isCompleted ? 'bg-[#15532E]' : 'bg-[#351E1D]/10'}`} />
              )}
            </div>
          )
        })}
      </div>

      <div className="bg-white rounded-xl p-6 border border-[#351E1D]/10">
        {activeStep === 'wntk' && (
          <CheckOutErrorBoundary stepName="Who Needs to Know What">
            <WNTKSection
              meetingId={meetingId}
              communications={meeting.communications || []}
              teamMembers={attendees}
              readOnly={readOnly}
            />
          </CheckOutErrorBoundary>
        )}
        {activeStep === 'ratings' && (
          <CheckOutErrorBoundary stepName="Rate This Meeting">
            <RatingsSection
              meetingId={meetingId}
              ratingsData={meeting.ratings_data || {}}
              attendees={attendees}
              readOnly={readOnly}
              currentUserId={currentUserId}
            />
          </CheckOutErrorBoundary>
        )}
        {activeStep === 'conflict' && (
          <CheckOutErrorBoundary stepName="Healthy Conflict">
            <HealthyConflictSection
              meetingId={meetingId}
              conflictResponses={meeting.conflict_responses || {}}
              attendees={attendees}
              readOnly={readOnly}
              currentUserId={currentUserId}
            />
          </CheckOutErrorBoundary>
        )}
        {activeStep === 'summary' && (
          <CheckOutErrorBoundary stepName="Meeting Summary">
            <MeetingSummarySection
              meetingId={meetingId}
              summaryRecipients={summaryRecipients || []}
            />
          </CheckOutErrorBoundary>
        )}
        {activeStep === 'end_meeting' && (
          <CheckOutErrorBoundary stepName="End Meeting">
            <EndMeetingSection
              meetingId={meetingId}
              canFacilitate={canFacilitate}
              readOnly={readOnly}
              meeting={meeting}
            />
          </CheckOutErrorBoundary>
        )}
      </div>

      {canFacilitate && (
        <div className="flex justify-between items-center pt-4">
          <button
            onClick={goPrev}
            disabled={activeStepIndex === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeStepIndex === 0 ? 'text-text-secondary/50 cursor-not-allowed' : 'text-text-primary hover:bg-[#351E1D]/5'
            }`}
            style={{ fontSize: '16px' }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>
          
          {activeStepIndex < STEPS.length - 1 ? (
            <button
              onClick={goNext}
              className="flex items-center gap-2 px-4 py-2 bg-[#351E1D] text-white rounded-lg font-medium hover:bg-[#351E1D]/90 transition-colors"
              style={{ fontSize: '16px' }}
            >
              Next
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : (
            <button
              onClick={handleEndMeeting}
              className="bg-pinnacle-red hover:bg-pinnacle-red/90 text-white font-semibold py-3 px-8 rounded-lg transition-colors text-base flex items-center gap-2 min-h-[44px]"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              End Meeting
            </button>
          )}
        </div>
      )}

      {!canFacilitate && (
        <p className="text-center text-sm text-text-muted italic">Only the facilitator can end the meeting.</p>
      )}
    </div>
  )
}

function WNTKSection({ meetingId, communications, teamMembers, readOnly }: {
  meetingId: number
  communications: WNTKEntry[]
  teamMembers: User[]
  readOnly?: boolean
}) {
  const [entries, setEntries] = useState<WNTKEntry[]>(
    communications.length > 0 ? communications : []
  )
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const entriesRef = useRef(entries)

  useEffect(() => {
    entriesRef.current = entries
  }, [entries])

  const persistEntries = useCallback((entriesToSave: WNTKEntry[]) => {
    setSaveStatus('saving')
    router.post(`/meetings/${meetingId}/save_communications`, {
      communications: entriesToSave.map(e => ({
        id: e.id,
        who_id: e.who_id,
        who_name: e.who_name,
        tells_whom: e.tells_whom,
        about_what: e.about_what,
      }))
    }, {
      preserveScroll: true,
      preserveState: true,
      // Partial reload limited to a tiny ack prop — UI is driven by optimistic
      // local state, so we don't need the full meeting payload back here.
      only: ['autosave_ack'],
      onSuccess: () => {
        setSaveStatus('saved')
        setTimeout(() => setSaveStatus(prev => prev === 'saved' ? 'idle' : prev), 2000)
      },
      onError: () => setSaveStatus('error'),
    })
  }, [meetingId])

  const scheduleSave = useCallback((updated: WNTKEntry[]) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      persistEntries(updated)
    }, 800)
  }, [persistEntries])

  const addEntry = () => {
    const updated = [...entries, {
      id: `new_${Date.now()}`,
      who_id: teamMembers[0]?.id || 0,
      who_name: teamMembers[0] ? `${teamMembers[0].first_name} ${teamMembers[0].last_name}` : '',
      tells_whom: '',
      about_what: ''
    }]
    setEntries(updated)
    persistEntries(updated)
  }

  const updateEntry = (index: number, field: keyof WNTKEntry, value: string | number) => {
    const updated = [...entries]
    if (field === 'who_id') {
      const member = teamMembers.find(m => m.id === Number(value))
      updated[index] = {
        ...updated[index],
        who_id: Number(value),
        who_name: member ? `${member.first_name} ${member.last_name}` : ''
      }
    } else if (field === 'tells_whom') {
      updated[index] = { ...updated[index], tells_whom: String(value) }
    } else if (field === 'about_what') {
      updated[index] = { ...updated[index], about_what: String(value) }
    } else if (field === 'who_name') {
      updated[index] = { ...updated[index], who_name: String(value) }
    }
    setEntries(updated)
    if (field === 'who_id') {
      persistEntries(updated)
    } else {
      scheduleSave(updated)
    }
  }

  const removeEntry = (index: number) => {
    const updated = entries.filter((_, i) => i !== index)
    setEntries(updated)
    persistEntries(updated)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-text-primary uppercase tracking-wide" style={{ fontSize: '14px' }}>Who Needs to Know What</h4>
          <p className="text-text-secondary mt-1" style={{ fontSize: '16px' }}>Identify key messages that need to be communicated after this meeting.</p>
        </div>
        <div className="flex items-center gap-3">
          {saveStatus === 'saving' && (
            <span className="text-text-secondary italic" style={{ fontSize: '13px' }}>Saving...</span>
          )}
          {saveStatus === 'saved' && (
            <span className="text-[#15532E] flex items-center gap-1" style={{ fontSize: '13px' }}>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Saved
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="text-[#ED1C24]" style={{ fontSize: '13px' }}>Save failed — try again</span>
          )}
          {!readOnly && (
            <button
              onClick={addEntry}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-charcoal text-white rounded-lg font-semibold hover:bg-charcoal/90 transition-colors"
              style={{ fontSize: '14px' }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Add Entry
            </button>
          )}
        </div>
      </div>

      {entries.length === 0 && (
        <div className="text-center py-6 bg-[#F5F5F5] rounded-lg">
          <p className="text-text-secondary" style={{ fontSize: '16px' }}>No communications added yet.</p>
          {!readOnly && (
            <p className="text-text-secondary mt-1" style={{ fontSize: '14px' }}>Click "Add Entry" to identify who needs to communicate what after this meeting.</p>
          )}
        </div>
      )}

      {entries.length > 0 && (
        <div className="space-y-3">
          {entries.map((entry, index) => (
            <div key={entry.id} className="bg-[#F5F5F5] rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-text-secondary mb-1" style={{ fontSize: '12px' }}>Who</label>
                  <select
                    value={entry.who_id}
                    onChange={e => updateEntry(index, 'who_id', e.target.value)}
                    disabled={readOnly}
                    className="w-full px-3 py-2 border border-[#351E1D]/10 rounded-lg bg-white text-text-primary"
                    style={{ fontSize: '14px' }}
                  >
                    {sortPeople(teamMembers).map(m => (
                      <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-text-secondary mb-1" style={{ fontSize: '12px' }}>Tells Whom</label>
                  <input
                    type="text"
                    value={entry.tells_whom}
                    onChange={e => updateEntry(index, 'tells_whom', e.target.value)}
                    disabled={readOnly}
                    placeholder="e.g. Sales team"
                    className="w-full px-3 py-2 border border-[#351E1D]/10 rounded-lg bg-white text-text-primary"
                    style={{ fontSize: '14px' }}
                  />
                </div>
                <div>
                  <label className="block text-text-secondary mb-1" style={{ fontSize: '12px' }}>About What</label>
                  <input
                    type="text"
                    value={entry.about_what}
                    onChange={e => updateEntry(index, 'about_what', e.target.value)}
                    disabled={readOnly}
                    placeholder="Key message..."
                    className="w-full px-3 py-2 border border-[#351E1D]/10 rounded-lg bg-white text-text-primary"
                    style={{ fontSize: '14px' }}
                  />
                </div>
              </div>
              {!readOnly && (
                <div className="flex justify-end">
                  <button
                    onClick={() => removeEntry(index)}
                    className="text-[#ED1C24] hover:text-pinnacle-red-dark transition-colors"
                    style={{ fontSize: '14px' }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function RatingsSection({ meetingId, ratingsData, attendees, readOnly, currentUserId }: {
  meetingId: number
  ratingsData: Record<string, number>
  attendees: User[]
  readOnly?: boolean
  currentUserId?: number
}) {
  const [localRatings, setLocalRatings] = useState<Record<string, number>>(ratingsData || {})
  const [savingFor, setSavingFor] = useState<string | null>(null)

  const handleRating = (userId: number, rating: number) => {
    const key = String(userId)
    setLocalRatings(prev => ({ ...prev, [key]: rating }))
    setSavingFor(key)

    router.post(`/meetings/${meetingId}/save_rating`, { user_id: userId, rating }, {
      preserveScroll: true,
      preserveState: true,
      // Partial reload limited to a tiny ack prop — UI is driven by optimistic
      // local state, so we don't need the full meeting payload back here.
      only: ['autosave_ack'],
      onError: () => {},
      onFinish: () => setSavingFor(null),
    })
  }

  const ratingValues = Object.values(localRatings).filter(v => typeof v === 'number') as number[]
  const avgRating = ratingValues.length > 0
    ? (ratingValues.reduce((sum, r) => sum + r, 0) / ratingValues.length).toFixed(1)
    : null

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-text-primary uppercase tracking-wide" style={{ fontSize: '14px' }}>Rate This Meeting</h4>
          <p className="text-text-secondary mt-1" style={{ fontSize: '16px' }}>
            Each participant rates 1 (poor) to 6 (excellent). {ratingValues.length}/{attendees.length} rated.
          </p>
        </div>
        {avgRating && (
          <div className="flex items-center gap-2 px-4 py-2 bg-[#351E1D]/10 rounded-lg">
            <span className="font-bold text-text-primary" style={{ fontSize: '24px' }}>{avgRating}</span>
            <span className="text-text-primary" style={{ fontSize: '14px' }}>/6 avg</span>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {/* Task #613: rating rows read alphabetically, like the rest of
            check-out. Sorting here keeps the order right whoever passes the
            attendees in. */}
        {sortPeople(attendees).map(user => {
          const key = String(user.id)
          const currentRating = localRatings[key]
          const isCurrentUser = user.id === currentUserId
          const isSaving = savingFor === key

          return (
            <div key={user.id} className="flex items-center gap-4 py-3 border-b border-[#351E1D]/5 last:border-0">
              <div className="w-40 flex-shrink-0">
                <span className="font-medium text-text-primary" style={{ fontSize: '16px' }}>
                  {user.first_name} {user.last_name}
                  {isCurrentUser && <span className="text-text-primary ml-1" style={{ fontSize: '12px' }}>(you)</span>}
                </span>
              </div>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5, 6].map(rating => (
                  <button
                    key={rating}
                    onClick={() => !readOnly && handleRating(user.id, rating)}
                    disabled={readOnly || isSaving}
                    className={`w-10 h-10 rounded-lg font-semibold transition-colors ${
                      currentRating === rating
                        ? 'bg-charcoal text-white'
                        : 'bg-[#351E1D]/5 text-text-primary hover:bg-[#351E1D]/20'
                    } ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
                    style={{ fontSize: '16px' }}
                  >
                    {rating}
                  </button>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function HealthyConflictSection({ meetingId, conflictResponses, attendees, readOnly, currentUserId }: {
  meetingId: number
  conflictResponses: Record<string, ConflictResponse>
  attendees: User[]
  readOnly?: boolean
  currentUserId?: number
}) {
  const [localResponses, setLocalResponses] = useState<Record<string, ConflictResponse>>(
    () => normalizeConflictResponses(conflictResponses)
  )

  const handleConflictResponse = (userId: number, value: ConflictResponse) => {
    const key = String(userId)
    setLocalResponses(prev => ({ ...prev, [key]: value }))

    router.post(`/meetings/${meetingId}/save_conflict_response`, { user_id: userId, conflict_response: value }, {
      preserveScroll: true,
      preserveState: true,
      // Partial reload limited to a tiny ack prop — UI is driven by optimistic
      // local state, so we don't need the full meeting payload back here.
      only: ['autosave_ack'],
      onError: () => {},
    })
  }

  const responses = Object.values(localResponses)
  const yesCount = responses.filter(v => v === 'yes').length
  const noCount = responses.filter(v => v === 'no').length
  const naCount = responses.filter(v => v === 'na').length

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-text-primary uppercase tracking-wide" style={{ fontSize: '14px' }}>Healthy Conflict</h4>
          <p className="text-text-secondary mt-1" style={{ fontSize: '16px' }}>
            Did we engage in healthy, productive conflict during this meeting?
          </p>
        </div>
        {responses.length > 0 && (
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 bg-[#15532E]/10 text-[#15532E] rounded-full font-semibold" style={{ fontSize: '14px' }}>
              Yes: {yesCount}
            </span>
            <span className="px-3 py-1 bg-[#ED1C24]/10 text-[#ED1C24] rounded-full font-semibold" style={{ fontSize: '14px' }}>
              No: {noCount}
            </span>
            <span className="px-3 py-1 bg-[#351E1D]/10 text-text-primary rounded-full font-semibold" style={{ fontSize: '14px' }}>
              N/A: {naCount}
            </span>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {/* Task #613: conflict rows read alphabetically, like the rest of
            check-out. Sorting here keeps the order right whoever passes the
            attendees in. */}
        {sortPeople(attendees).map(user => {
          const currentResponse = localResponses[String(user.id)]
          const isCurrentUser = user.id === currentUserId

          return (
            <div key={user.id} className="flex items-center gap-4 py-3 border-b border-[#351E1D]/5 last:border-0">
              <div className="w-40 flex-shrink-0">
                <span className="font-medium text-text-primary" style={{ fontSize: '16px' }}>
                  {user.first_name} {user.last_name}
                  {isCurrentUser && <span className="text-text-primary ml-1" style={{ fontSize: '12px' }}>(you)</span>}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => !readOnly && handleConflictResponse(user.id, 'yes')}
                  disabled={readOnly}
                  className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                    currentResponse === 'yes'
                      ? 'bg-[#15532E] text-white'
                      : 'bg-[#351E1D]/5 text-text-primary hover:bg-[#15532E]/20'
                  } ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
                  style={{ fontSize: '16px' }}
                >
                  Yes
                </button>
                <button
                  onClick={() => !readOnly && handleConflictResponse(user.id, 'no')}
                  disabled={readOnly}
                  className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                    currentResponse === 'no'
                      ? 'bg-[#ED1C24] text-white'
                      : 'bg-[#351E1D]/5 text-text-primary hover:bg-[#ED1C24]/20'
                  } ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
                  style={{ fontSize: '16px' }}
                >
                  No
                </button>
                {/* Task #645: N/A for people who can't answer the question.
                    Neutral charcoal when selected so it reads as "no opinion"
                    rather than a win or a miss. */}
                <button
                  onClick={() => !readOnly && handleConflictResponse(user.id, 'na')}
                  disabled={readOnly}
                  className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                    currentResponse === 'na'
                      ? 'bg-[#351E1D] text-white'
                      : 'bg-[#351E1D]/5 text-text-primary hover:bg-[#351E1D]/20'
                  } ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
                  style={{ fontSize: '16px' }}
                >
                  N/A
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function MeetingSummarySection({ meetingId, summaryRecipients }: {
  meetingId: number
  summaryRecipients: { id: number; name: string; email: string }[]
}) {
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [previewState, setPreviewState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [sampleStatus, setSampleStatus] = useState<'idle' | 'sending'>('idle')
  const [deliveryMode, setDeliveryMode] = useState<string | null>(null)

  const loadPreview = useCallback(() => {
    setPreviewState('loading')
    fetch(`/meetings/${meetingId}/summary_preview`, { // allow-direct-fetch: read-only GET of server-rendered recap email preview HTML
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        if (res.redirected) throw new Error('Session expired')
        setDeliveryMode(res.headers.get('X-Recap-Delivery-Mode'))
        return res.text()
      })
      .then(html => {
        setPreviewHtml(html)
        setPreviewState('ready')
      })
      .catch(() => {
        setPreviewHtml(null)
        setPreviewState('error')
      })
  }, [meetingId])

  useEffect(() => {
    loadPreview()
  }, [loadPreview])

  const sendSample = () => {
    setSampleStatus('sending')
    router.post(`/meetings/${meetingId}/send_summary_preview`, {}, {
      preserveScroll: true,
      preserveState: true,
      onFinish: () => setSampleStatus('idle'),
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h4 className="font-semibold text-text-primary uppercase tracking-wide" style={{ fontSize: '14px' }}>Recap Email Preview</h4>
        <button
          onClick={sendSample}
          disabled={sampleStatus === 'sending'}
          className="flex items-center gap-2 px-3 py-2 rounded-lg font-medium border border-[#351E1D]/15 text-text-primary hover:bg-[#351E1D]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ fontSize: '14px' }}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
           {sampleStatus === 'sending' ? 'Queueing…' : 'Send a sample to me'}
        </button>
      </div>

      <div className="bg-[#351E1D]/5 rounded-lg p-4">
        <p className="font-semibold text-text-primary mb-2" style={{ fontSize: '14px' }}>
          Recap recipients: {summaryRecipients.length} {summaryRecipients.length === 1 ? 'person' : 'people'}
        </p>
        <p className="text-text-secondary mb-3" style={{ fontSize: '13px' }}>
          When email delivery is enabled, everyone on this meeting's roster receives the recap, including absent attendees. Deactivated accounts and invalid email addresses are excluded.
        </p>
        {summaryRecipients.length === 0 ? (
          <p className="text-text-muted italic" style={{ fontSize: '14px' }}>No eligible recipients on the meeting roster. The recap will be skipped.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {sortPeople(summaryRecipients).map(r => (
              <div key={r.id} className="flex flex-col py-1.5 px-3 bg-white rounded-lg border border-[#351E1D]/5">
                <span className="font-medium text-text-primary" style={{ fontSize: '14px' }}>{r.name || r.email}</span>
                <span className="text-text-muted" style={{ fontSize: '13px' }}>{r.email}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="text-text-muted mb-2" style={{ fontSize: '13px' }}>
          {deliveryMode === 'dynamic_template'
            ? 'SendGrid dynamic-template mode: this is the current Pinnacle HTML. The external active template must use the supplied recap template asset; its appearance has not been verified.'
            : deliveryMode === 'not_configured'
              ? 'Email is not configured. This read-only preview shows the current Pinnacle HTML; no email has been sent.'
              : 'Inline HTML mode: this is the reusable email body. Live timing is unfinished until the meeting ends; email clients may render fonts differently.'}
        </p>
        <div className="rounded-lg border border-[#351E1D]/10 overflow-hidden bg-[#F8F9FA]" style={{ height: '600px' }}>
          {previewState === 'loading' && (
            <div className="h-full flex flex-col items-center justify-center text-text-muted">
              <svg className="w-6 h-6 animate-spin mb-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span style={{ fontSize: '14px' }}>Loading preview…</span>
            </div>
          )}
          {previewState === 'error' && (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <p className="text-[#C41920] font-medium mb-2" style={{ fontSize: '14px' }}>Couldn’t load the recap preview.</p>
              <button
                onClick={loadPreview}
                className="text-text-primary underline hover:no-underline"
                style={{ fontSize: '14px' }}
              >
                Try again
              </button>
            </div>
          )}
          {previewState === 'ready' && previewHtml !== null && (
            <iframe
              title="Recap email preview"
              srcDoc={previewHtml}
              sandbox=""
              className="w-full h-full border-0 bg-white"
            />
          )}
        </div>
      </div>
    </div>
  )
}

function EndMeetingSection({ meetingId, canFacilitate, readOnly, meeting }: {
  meetingId: number
  canFacilitate: boolean
  readOnly?: boolean
  meeting: Meeting
}) {
  const handleEndMeeting = () => {
    if (confirm('This will end the meeting and send the summary email. Continue?')) {
      router.post(`/meetings/${meetingId}/end_meeting`)
    }
  }

  if (readOnly) {
    return (
      <div className="text-center py-8">
        <svg className="w-12 h-12 mx-auto mb-3 text-[#15532E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h4 className="text-lg font-semibold text-text-primary mb-1">Meeting Completed</h4>
        <p className="text-sm text-text-secondary">
          This meeting ended on {meeting.ended_at ? new Date(meeting.ended_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'N/A'}.
        </p>
        {meeting.summary_sent_at && (
          <p className="text-sm text-text-muted mt-2">Summary email sent</p>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg p-4 border border-stone/10">
        <h5 className="text-sm font-semibold text-text-muted uppercase tracking-wide mb-3">What happens when you end the meeting:</h5>
        <ul className="space-y-2 text-sm text-text-primary">
          <li className="flex items-start gap-2">
            <svg className="w-4 h-4 text-pinnacle-red mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            All pending sections will be marked completed
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-4 h-4 text-pinnacle-red mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            Meeting summary email will be sent to all recipients
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-4 h-4 text-pinnacle-red mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            Average rating will be calculated and saved
          </li>
        </ul>
      </div>

      {canFacilitate && (
        <div className="flex justify-center pt-2">
          <button
            onClick={handleEndMeeting}
            className="bg-pinnacle-red hover:bg-pinnacle-red/90 text-white font-semibold py-3 px-8 rounded-lg transition-colors text-base flex items-center gap-2 min-h-[44px]"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            End Meeting
          </button>
        </div>
      )}

      {!canFacilitate && (
        <p className="text-center text-sm text-text-muted italic">Only the facilitator can end the meeting.</p>
      )}
    </div>
  )
}
